import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Settings, MessageSquarePlus } from 'lucide-react';
import FeedbackModal from './FeedbackModal';
import ReleaseNotesModal from './ReleaseNotesModal';
import EinstellungenDialog from './einstellungen/EinstellungenDialog';
import Leisten from './header/Leisten';
import BurgerMenue from './header/BurgerMenue';
import Wortmarke from './header/Wortmarke';
import Reiterleiste from './header/Reiterleiste';
import Glocke from './header/Glocke';
import OptionenMenue from './header/OptionenMenue';
import './header/kopfzeile.css'; // #562: Stile der Kopfzeile (vorher inline)
import { getFeatures } from '../utils/featuresCache';
import apiService from '../services/apiService';
import * as loadingDebug from '../utils/loadingDebug';
import { ensurePushSubscription, disablePushSubscription } from '../utils/pushManager';
import useIsMobile from '../hooks/useIsMobile';
import KontaktSuche from './KontaktSuche';
import useAutoCache from '../hooks/useAutoCache';
import { useI18n } from '../utils/i18n';
import { ladeEinstellungen, useBenutzerEinstellungen } from '../services/benutzerEinstellungen';

const Header = () => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [setupRequired, setSetupRequired] = useState(false);
  // NEW: bell state
  const [hasUnread, setHasUnread] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevHasUnreadRef = useRef(false);
  const [attentionTickets, setAttentionTickets] = useState([]);
  const [showBellDropdown, setShowBellDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  // W6 (#16 A2.6): Burger-Menü links — macht mobil ALLE Ansichten erreichbar
  // (Beschluss auf #16: Burger links, Optionen-Menü rechts)
  const [showBurgerMenu, setShowBurgerMenu] = useState(false);
  // #42: Feedback-Dialog (Button links neben der Glocke)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // #85: „Was ist neu?" aus dem Optionen-Menü
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  // #567: Einstellungs-Dialog aus dem Optionen-Menü (darin auch die API-Keys, #80)
  const [showEinstellungen, setShowEinstellungen] = useState(false);
  // #87: Kunden-Feature-Flag für den Menüpunkt Kundenverwaltung
  const [customersEnabled, setCustomersEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getFeatures().then(features => {
      if (!cancelled) setCustomersEnabled(features.customers !== false);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const bellRef = useRef(null);
  const userRef = useRef(null);
  const burgerRef = useRef(null);
  const isMobile = useIsMobile();
  const audioRef = useRef(null);
  const [showGlobalDropOverlay, setShowGlobalDropOverlay] = useState(false);
  const dragDepthRef = useRef(0);
  const isFileDragRef = useRef(false);
  const navigate = useNavigate();
  const hasAuthRetryRef = useRef(false);

  // NEW: Desktop notification support + permission state
  const [hasNotificationSupport, setHasNotificationSupport] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [globalNotice, setGlobalNotice] = useState({ isActive: false, message: '' });
  const location = useLocation();
  const [projectPlanInfo, setProjectPlanInfo] = useState({ id: null, name: '' });
  const isProjectPlanningMode = !!(projectPlanInfo.id && location.pathname.includes('/global-calendar'));
  const { t } = useI18n();
  // #568: Reiter, „nur Symbol“ und Kontaktsuche aus den Benutzer-Einstellungen.
  // Die Reiter erscheinen erst mit dem Stand, sonst blitzen ausgeblendete beim
  // Laden kurz auf. Ohne Stand (Fehler) gilt die Vorgabe: alles an.
  const einstellungen = useBenutzerEinstellungen();
  const [ohneEinstellungen, setOhneEinstellungen] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return;
    ladeEinstellungen().catch(() => setOhneEinstellungen(true));
  }, [isAuthenticated]);
  const kopfEinstellungen = einstellungen || (ohneEinstellungen ? {} : null);
  // #569: Das leere Dashboard öffnet den Einstellungs-Dialog über ein Ereignis
  useEffect(() => {
    const oeffnen = () => setShowEinstellungen(true);
    window.addEventListener('einstellungenOeffnen', oeffnen);
    return () => window.removeEventListener('einstellungenOeffnen', oeffnen);
  }, []);
  const ausgeblendeteReiter = kopfEinstellungen?.reiter;
  const kontaktsucheSichtbar = customersEnabled && (user?.dbUser?.isManager || user?.dbUser?.isAdmin) && kopfEinstellungen?.kontaktsuche !== false;
  const [loadingDebugItems, setLoadingDebugItems] = useState([]);

  useEffect(() => {
    checkAuthStatus();

    // Bei erfolgreichem Token-Refresh Auth-Status erneut laden
    const onTokenRefreshSuccess = () => {
      try { checkAuthStatus(); } catch (_) {}
    };

    // Vor automatischem Re-Login optional Zustand zurücksetzen
    const onAutomaticReloginStarted = () => {
      try {
        setUser(null);
        setIsAuthenticated(false);
      } catch (_) {}
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('tokenRefreshSuccess', onTokenRefreshSuccess);
      window.addEventListener('automaticReloginStarted', onAutomaticReloginStarted);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('tokenRefreshSuccess', onTokenRefreshSuccess);
        window.removeEventListener('automaticReloginStarted', onAutomaticReloginStarted);
      }
    };
  }, []);

  // Subscribe to global loading debug updates
  useEffect(() => {
    let unsubscribe = null;
    try {
      unsubscribe = loadingDebug.subscribe(setLoadingDebugItems);
    } catch (_) {}
    return () => { try { unsubscribe && unsubscribe(); } catch (_) {} };
  }, []);

  useEffect(() => {
    // Detect notification capability and current permission
    try {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        setHasNotificationSupport(true);
        setNotificationPermission(window.Notification.permission);
      }
    } catch (_) {}
  }, []);

  // Erkenne Projekt-Zeitplan-Modus aus der URL
  useEffect(() => {
    // Bei Routenwechsel (insbesondere nach Login-Redirect) Auth-Status neu evaluieren
    try { checkAuthStatus(); } catch (_) {}

    try {
      const params = new URLSearchParams(location.search || '');
      const planId = params.get('projectPlanId');
      const planName = params.get('projectName') || '';
      if (planId) {
        setProjectPlanInfo({ id: planId, name: decodeURIComponent(planName) });
      } else {
        setProjectPlanInfo({ id: null, name: '' });
      }
    } catch (_) {
      setProjectPlanInfo({ id: null, name: '' });
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.Audio !== 'undefined') {
        const el = new window.Audio('/notify.mp3');
        el.preload = 'auto';
        el.volume = 1.0;
        audioRef.current = el;
      }
    } catch (_) {}
  }, []);

  // Load global notification bar config periodically (only when authenticated)
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let timerId = null;

    const loadNotice = async () => {
      try {
        const resp = await apiService.getNotificationConfig();
        if (!cancelled) {
          setGlobalNotice(resp?.config || { isActive: false, message: '' });
        }
      } catch (_) {}
    };

    loadNotice();
    timerId = setInterval(loadNotice, 60 * 1000);

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const onAttention = (e) => {
      const detail = e?.detail || {};
      const nextHasUnread = !!detail.hasUnread;
      const nextCount = Number(detail.unreadCount || 0);
      const nextTickets = Array.isArray(detail.tickets) ? detail.tickets : [];

      // Play sound only when transitioning from no-unread to has-unread
      if (!prevHasUnreadRef.current && nextHasUnread) {
        try {
          let el = audioRef.current;
          if (!el && typeof window !== 'undefined' && typeof window.Audio !== 'undefined') {
            el = new window.Audio('/notify.mp3');
            el.preload = 'auto';
            el.volume = 1.0;
            audioRef.current = el;
          }
          if (el) {
            try { el.currentTime = 0; } catch (_) {}
            const p = el.play();
            if (p && typeof p.then === 'function') p.catch(() => {});
          }
        } catch (_) {}
      }

      prevHasUnreadRef.current = nextHasUnread;
      setHasUnread(nextHasUnread);
      setUnreadCount(nextCount);
      setAttentionTickets(nextTickets);
    };

    window.addEventListener('ticketsAttentionUpdate', onAttention);
    return () => window.removeEventListener('ticketsAttentionUpdate', onAttention);
  }, []);

  const checkAuthStatus = async () => {
    try {
      const healthResponse = await apiService.getHealth();
      setSetupRequired(!healthResponse.configured);
      if (healthResponse.authenticated) {
        setIsAuthenticated(true);
        if (healthResponse.configured) {
          try {
            const userResponse = await apiService.getUser();
            setUser(userResponse);
          } catch (_) {}
        }
        return;
      }
      // Kein Fallback mehr: wenn nicht authentifiziert, einfach Zustand setzen
      setIsAuthenticated(false);
      setUser(null);

      // Edge-Case: Direkt nach Login kann die Session-Cookie-Übernahme minimal verzögert sein.
      // Einmaliger kurzer Retry, wenn bereits eingeloggt war (Cookie) und wir auf dem Dashboard stehen.
      try {
        const raw = String(document.cookie || '');
        const wasLogged = raw.split(';').some(c => c.trim().startsWith('was_logged_in=1'));
        const isDashboard = typeof window !== 'undefined' && window.location.pathname.startsWith('/dashboard');
        if (wasLogged && isDashboard && !hasAuthRetryRef.current) {
          hasAuthRetryRef.current = true;
          setTimeout(() => { try { checkAuthStatus(); } catch (_) {} }, 500);
        }
      } catch (_) {}
    } catch (error) {
      console.error('Error checking auth status:', error);
    }
  };

  // #16 B6: Gerät für Web Push registrieren, sobald Benachrichtigungen
  // erlaubt sind (idempotent; im Dev-Modus ohne SW ein stiller No-op).
  useEffect(() => {
    if (!isAuthenticated || notificationPermission !== 'granted') return;
    ensurePushSubscription();
  }, [isAuthenticated, notificationPermission]);

  const handleLogout = async () => {
    try {
      // #16 B6: Gerät abmelden — sonst kämen Pushes ohne Session weiter an
      try { await disablePushSubscription(); } catch (_) {}
      await apiService.logout();
      setUser(null);
      setIsAuthenticated(false);
      // Nach Logout: kein Auto-SSO mehr, zurück zum Willkommen-Screen
      window.location.href = '/';
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Global Tickets-Live-Check: nur auf relevanten Routen und wenn Seite sichtbar
  const [isPageVisible, setIsPageVisible] = useState(true);
  useEffect(() => {
    const onVis = () => {
      try { setIsPageVisible(!document.hidden); } catch (_) {}
    };
    try { document.addEventListener('visibilitychange', onVis); } catch (_) {}
    return () => { try { document.removeEventListener('visibilitychange', onVis); } catch (_) {} };
  }, []);

  // Avoid duplicate ticket polling: dashboard/tickets pages already run their own poller
  const shouldHeaderPollTickets = (() => {
    try {
      const p = location.pathname || '';
      // Do NOT poll on pages that already mount their own tickets auto-cache
      if (p === '/dashboard') return false;
      if (p.startsWith('/tickets')) return false;
      if (p === '/pool') return false; // Pool-Monitor pollt selbst (W6)
      // Poll tickets on all other pages so that bell/desktop notifications are global
      return true;
    } catch (_) { return false; }
  })();

  const ticketPollInterval = useMemo(() => 60000 + Math.floor(Math.random() * 5000), []); // Increased from 10-15s to 60-65s to prevent pool exhaustion
  useAutoCache({ type: 'tickets', enabled: isAuthenticated && shouldHeaderPollTickets && isPageVisible, onDataUpdate: null, checkInterval: ticketPollInterval });

  const handleBellClick = (e) => {
    e.preventDefault();
    setShowBellDropdown((prev) => !prev);
  };

  const handleOpenTicketDetailModal = async (ticketId) => {
    try {
      // Optimistisch: Entferne Eintrag aus Glockenliste und dekrementiere Zähler
      setAttentionTickets(prev => {
        const next = prev.filter(t => t.id !== ticketId);
        setUnreadCount(c => Math.max(0, (c || 0) - 1));
        setHasUnread(next.length > 0 || (hasNotificationSupport && notificationPermission !== 'granted'));
        return next;
      });
    } catch (_) {}
    try { await apiService.markTicketAsViewed(ticketId); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'ticketViewedViaHeader', ticketId } })); } catch (_) {}
    try {
      window.dispatchEvent(new CustomEvent('openTicketDetailById', { detail: { ticketId } }));
      setShowBellDropdown(false);
    } catch (_) {}
  };

  // NEW: request desktop notifications on user gesture (supports promise & callback styles)
  const requestDesktopNotifications = async () => {
    try {
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      let perm = null;
      try {
        const possiblePromise = window.Notification.requestPermission((p) => { try { perm = p; } catch (_) {} });
        if (possiblePromise && typeof possiblePromise.then === 'function') {
          perm = await possiblePromise;
        }
      } catch (e) {
        // Fallback: direct permission check
        try { perm = window.Notification.permission; } catch (_) {}
      }
      if (!perm) {
        try { perm = window.Notification.permission; } catch (_) {}
      }
      if (perm) setNotificationPermission(perm);
      if (perm === 'granted') {
        try {
          new window.Notification(t('header.notificationsEnabled', 'Benachrichtigungen aktiviert'), {
            body: t('header.notificationsEnabledBody', 'Sie erhalten jetzt Desktop-Benachrichtigungen.'),
            silent: true
          });
        } catch (_) {}
        try { setShowBellDropdown(false); } catch (_) {}
      }
    } catch (_) {}
  };

  // Bell indicators include notification activation prompt as an attention item
  const notificationActivationPending = hasNotificationSupport && notificationPermission !== 'granted';
  const displayedUnreadCount = (unreadCount || 0) + (notificationActivationPending ? 1 : 0);
  const bellAttention = hasUnread || notificationActivationPending;

  // Close dropdowns on outside click/touch
  useEffect(() => {
    const handleOutside = (e) => {
      try {
        if (showBellDropdown && bellRef.current && !bellRef.current.contains(e.target)) {
          setShowBellDropdown(false);
        }
        if (showUserDropdown && userRef.current && !userRef.current.contains(e.target)) {
          setShowUserDropdown(false);
        }
        if (showBurgerMenu && burgerRef.current && !burgerRef.current.contains(e.target)) {
          setShowBurgerMenu(false);
        }
      } catch (_) {}
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showBellDropdown, showUserDropdown, showBurgerMenu]);

  // Global Drag & Drop to create a new ticket with files when TicketDetail is not open
  useEffect(() => {
    const hasFiles = (e) => {
      try {
        const types = e?.dataTransfer?.types;
        if (!types) return false;
        return Array.from(types).includes('Files');
      } catch (_) {
        return false;
      }
    };

    const isTicketDetailOpen = () => {
      try {
        return !!document.querySelector('.ticket-detail-overlay');
      } catch (_) {
        return false;
      }
    };

    const resetDragState = () => {
      dragDepthRef.current = 0;
      isFileDragRef.current = false;
      setShowGlobalDropOverlay(false);
    };

    const onDragEnter = (e) => {
      try {
        if (isTicketDetailOpen()) return;
        const containsFiles = hasFiles(e);
        if (!containsFiles && !isFileDragRef.current) return;
        if (containsFiles) isFileDragRef.current = true;
        e.preventDefault();
        dragDepthRef.current = (dragDepthRef.current || 0) + 1;
        setShowGlobalDropOverlay(true);
      } catch (_) {}
    };

    const onDragOver = (e) => {
      try {
        if (isTicketDetailOpen()) return;
        if (!isFileDragRef.current && !hasFiles(e)) return;
        e.preventDefault();
        setShowGlobalDropOverlay(true);
      } catch (_) {}
    };

    const onDrop = (e) => {
      try {
        if (isTicketDetailOpen()) return;
        if (!isFileDragRef.current && !hasFiles(e)) return;
        e.preventDefault();
        e.stopPropagation();
      } catch (_) {}
      // Always reset overlay and counters on drop
      try { resetDragState(); } catch (_) {}
      try {
        const files = Array.from(e.dataTransfer?.files || []);
        if (!files || files.length === 0) return;
        try { window.__pendingCreateTicketAttachments = files; } catch (_) {}
        try { window.dispatchEvent(new CustomEvent('openCreateTicketWithPendingAttachments', { detail: { fileCount: files.length } })); } catch (_) {}
        try {
          const isOnDashboard = window.location.pathname.startsWith('/dashboard');
          if (!isOnDashboard) {
            navigate('/dashboard');
            setTimeout(() => {
              try { window.dispatchEvent(new CustomEvent('openCreateTicketWithPendingAttachments', { detail: { fileCount: files.length } })); } catch (_) {}
            }, 0);
          }
        } catch (_navErr) {
          try { if (!window.location.pathname.startsWith('/dashboard')) window.location.href = '/dashboard'; } catch (_) {}
        }
      } catch (_) {}
    };

    const onDragLeave = (e) => {
      try {
        if (isTicketDetailOpen()) return;
        if (!isFileDragRef.current) return;
        dragDepthRef.current = Math.max(0, (dragDepthRef.current || 0) - 1);
        if (dragDepthRef.current === 0) {
          isFileDragRef.current = false;
          setShowGlobalDropOverlay(false);
        }
      } catch (_) {}
    };

    const onDragEnd = () => {
      try { resetDragState(); } catch (_) {}
    };

    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('dragend', onDragEnd);

    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('dragend', onDragEnd);
    };
  }, [navigate]);

  return (
    <>
      <Leisten
        t={t} navigate={navigate} isProjectPlanningMode={isProjectPlanningMode} projectPlanInfo={projectPlanInfo}
        showGlobalDropOverlay={showGlobalDropOverlay} globalNotice={globalNotice}
      />
      <header className="header">
      <div className="container">
        <div className="header-content">
          {/* W6 (#16 A2.6): Burger-Menü links auf Mobil — alle Ansichten mit
              denselben Rollen-Regeln wie die Desktop-Nav (#44) */}
          {isMobile && isAuthenticated && !setupRequired && (
            <BurgerMenue user={user} t={t} offen={showBurgerMenu} setOffen={setShowBurgerMenu} burgerRef={burgerRef} ausgeblendet={ausgeblendeteReiter} />
          )}
          <h1 className={isMobile ? 'kopf-titel--mobil' : undefined}>
            {/* #565: KayCee-Wortmarke; der sichtbare Name ist Text, damit Screenreader „KayCee Ticket“ lesen */}
            <Link to="/" className="kopf-titel-link" aria-label="KayCee Ticket">
              <Wortmarke kompakt={isMobile} />
            </Link>
          </h1>
          {/* #255: Kontaktsuche -- gleiche Sichtbarkeit wie der Link zur Kundenverwaltung.
              #568: direkt neben dem Logo, in den Einstellungen abschaltbar */}
          {isAuthenticated && !setupRequired && kontaktsucheSichtbar && (
            <div className={`kopf-kontaktsuche${isMobile ? ' kopf-kontaktsuche--mobil' : ''}`}>
              <KontaktSuche isMobile={isMobile} />
            </div>
          )}

          <nav className={`header-nav${isMobile ? ' kopf-nav--mobil' : ''}`}>
            {isAuthenticated && !setupRequired ? (
              <>
                {/* #562: Reiter aus dem Register (header/reiter.js); mobil im Burger-Menü */}
                {!isMobile && kopfEinstellungen && (
                  <Reiterleiste user={user} t={t} ausgeblendet={ausgeblendeteReiter} nurSymbol={kopfEinstellungen.reiterNurSymbol === true} />
                )}

                <div className={`kopf-aktionen${isMobile ? ' kopf-aktionen--mobil' : ''}`}>
                  {/* #512: Hier kommt der Knopf der KI-Seitenleiste hin (KayCee-Kopf,
                      header/KayCeeKopf), links neben Feedback und Glocke */}
                  {/* #42: Feedback-Button links neben der Glocke */}
                    <button
                      onClick={() => setShowFeedbackModal(true)}
                      title={t('feedback.buttonTitle', 'Feedback geben (Fehler oder Wunsch melden)')} aria-label={t('feedback.buttonTitle', 'Feedback geben (Fehler oder Wunsch melden)')}
                      className="kopf-knopf kopf-knopf--hover"
                    >
                      <MessageSquarePlus size={18} />
                    </button>
                  <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
                  {/* #85: „Was ist neu?"-Modal (Versions-History) */}
                  <ReleaseNotesModal open={showReleaseNotes} onClose={() => setShowReleaseNotes(false)} />
                  <EinstellungenDialog open={showEinstellungen} onClose={() => setShowEinstellungen(false)} user={user} />
                  {/* NEW: Bell — W6: auf allen Routen sichtbar (F5) */}
                  <Glocke
                    t={t} isMobile={isMobile} bellRef={bellRef} handleBellClick={handleBellClick}
                    bellAttention={bellAttention} displayedUnreadCount={displayedUnreadCount} showBellDropdown={showBellDropdown}
                    loadingDebugItems={loadingDebugItems} notificationActivationPending={notificationActivationPending}
                    attentionTickets={attentionTickets} requestDesktopNotifications={requestDesktopNotifications}
                    notificationPermission={notificationPermission} handleOpenTicketDetailModal={handleOpenTicketDetailModal}
                  />

                  <div className="kopf-optionen-gruppe">
                    {/* #81: Optionen-Menü (bündelt Benutzer, Sprache, Administration, Abmelden;
                        Glocke und Feedback bleiben bewusst außerhalb) */}
                    <OptionenMenue
                      t={t} isMobile={isMobile} user={user}
                      userRef={userRef} showUserDropdown={showUserDropdown} setShowUserDropdown={setShowUserDropdown}
                      setShowReleaseNotes={setShowReleaseNotes} setShowEinstellungen={setShowEinstellungen}
                      customersEnabled={customersEnabled} handleLogout={handleLogout} isProjectPlanningMode={isProjectPlanningMode}
                    />
                    {/* #81: Der frühere Admin-Zahnrad-Link ist ins Optionen-Menü gewandert */}
                  </div>
                </div>
                
              </>
            ) : setupRequired ? (
              <Link 
                to="/setup"
                className="kopf-reiter"
              >
                <Settings size={16} />
                {t('header.setup', 'Setup')}
              </Link>
            ) : null}
          </nav>
        </div>
      </div>
    </header>
    </>
  );
};

export default Header; 